import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelect } from "@/components/ui/multi-select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useFreelancersInvalidate } from "@/hooks/use-freelancers-invalidate";
import { reportUserError } from "@/lib/report-error";

// Cadastro mínimo: só o suficiente para o freelancer existir e poder ser
// associado a uma vaga/projeto depois. `nome` e `email` são os únicos NOT NULL
// na tabela; o resto é opcional. As áreas usam o vocabulário canônico de
// `areas_atuacao` (mesma fonte do form de edição) para o overlap do engine de
// recomendação funcionar. O endereço completo vai no campo livre `cidade`,
// igual ao comportamento atual da UI (label "Endereço").
const formSchema = z.object({
  nome: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  email: z.string().email("Email inválido"),
  areas: z.array(z.string()).optional(),
  telefone: z.string().optional(),
  endereco: z.string().optional(),
  is_active: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

interface NovoFreelancerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NovoFreelancerDialog({ open, onOpenChange }: NovoFreelancerDialogProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const invalidateFreelancers = useFreelancersInvalidate();
  const [areasCanonicas, setAreasCanonicas] = useState<{ id: string; nome: string }[]>([]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nome: "",
      email: "",
      areas: [],
      telefone: "",
      endereco: "",
      is_active: false, // inativo por padrão (cadastro manual = lead a validar)
    },
  });

  // Áreas canônicas ativas — mesma fonte que o form de edição e o dropdown de vaga.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("areas_atuacao")
        .select("id, nome")
        .eq("is_active", true)
        .order("nome");
      if (error) {
        console.error("Erro ao buscar áreas de atuação:", error);
        return;
      }
      if (!cancelled && data) setAreasCanonicas(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Limpa o form sempre que o dialog reabre.
  useEffect(() => {
    if (open) form.reset();
  }, [open, form]);

  async function onSubmit(values: FormValues) {
    const email = values.email.trim().toLowerCase();
    const insertData = {
      nome: values.nome.trim(),
      email,
      areas: values.areas ?? [],
      telefone: values.telefone?.trim() || null,
      cidade: values.endereco?.trim() || null,
      is_active: values.is_active,
    };

    const { data, error } = await supabase
      .from("freelancers")
      .insert(insertData)
      .select("id")
      .single();

    if (error) {
      // 23505 = unique_violation → email já cadastrado (índice case-insensitive
      // uq_freelancers_email_lower, migration 122).
      const isDuplicate = error.code === "23505" || /duplicate|unique/i.test(error.message);
      toast({
        variant: "destructive",
        title: isDuplicate ? "Email já cadastrado" : "Erro ao criar freelancer",
        description: isDuplicate
          ? "Já existe um freelancer com este email. Busque pelo email na listagem."
          : reportUserError(error, { source: "freelancers" }).description,
      });
      return;
    }

    toast({
      title: "Freelancer criado",
      description: values.is_active
        ? "Cadastro criado e ativo."
        : "Cadastro criado como inativo.",
    });

    invalidateFreelancers();
    onOpenChange(false);

    if (data?.id) navigate(`/freelancers/${data.id}`);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Novo Freelancer</DialogTitle>
          <DialogDescription>
            Cadastro mínimo para já poder associar a uma vaga ou projeto. Os
            demais dados podem ser completados depois pelo perfil.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nome completo" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="email@exemplo.com" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="areas"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Áreas de Atuação</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={areasCanonicas.map((a) => ({ label: a.nome, value: a.nome }))}
                      selected={field.value ?? []}
                      onChange={(values) => field.onChange(values)}
                      placeholder={
                        areasCanonicas.length === 0
                          ? "Carregando áreas..."
                          : "Selecione uma ou mais áreas"
                      }
                      disabled={areasCanonicas.length === 0}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="telefone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="(00) 00000-0000" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="endereco"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Endereço</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Rua, número, CEP, bairro, cidade - UF" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Criar como ativo</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Por padrão o cadastro é criado inativo. Marque para ativar já.
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={form.formState.isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Criando..." : "Criar Freelancer"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
